import { blankWizard } from "@/components/wizard/config";

const txt = (v) => (Array.isArray(v) ? v.filter((x) => x !== null && x !== "").map(txt).join(", ") : v === true ? "Yes" : v === false ? "No" : v == null ? "" : String(v));
const teeth = (c) => [...new Set([...(c.tooth_findings || []).map((t) => `${t.fdi}${t.status ? ` ${t.status}` : ""}`), ...(c.sites || []).filter((s) => s.site).map((s) => `${s.site}`)])].join(", ");
const ve = (c) => c.voice_exam || {};
const tmj = (c) => ve(c).tmj || {};

const COMMON_START = [
  { key: "name", label: "Patient name", mandatory: true, get: (c) => (c.patient_name && c.patient_name !== "New Patient" ? c.patient_name : "") },
  { key: "complaint", label: "Chief complaint", mandatory: true, get: (c) => c.chief_complaint },
];
const COMMON_END = [
  { key: "medical", label: "Medical history", get: (c) => txt(c.medical_history) },
  { key: "medications", label: "Current medications", get: (c) => txt(ve(c).medications) },
  { key: "allergies", label: "Allergies", get: (c) => txt(ve(c).allergies) },
  { key: "expectations", label: "Patient expectations", get: (c) => c.patient_expectations },
];

export const VOICE_TOPICS = {
  implant: [...COMMON_START,
    { key: "teeth", label: "Implant sites / teeth (FDI)", mandatory: true, hint: "e.g. 36, 46 missing", get: teeth },
    { key: "smoking", label: "Smoking", get: (c) => ve(c).smoking },
    { key: "soft", label: "Soft-tissue phenotype", get: (c) => c.soft_tissue },
    { key: "smile", label: "Smile line", get: (c) => ve(c).exam?.smile_line },
    { key: "measure", label: "Site measurements (mm)", get: (c) => (c.sites || []).filter((s) => s.md || s.bl || s.vertical).map((s) => `${s.site}: ${[s.md && `MD ${s.md}`, s.bl && `BL ${s.bl}`, s.vertical && `V ${s.vertical}`].filter(Boolean).join(" ")}`).join("; ") },
    ...COMMON_END],
  general: [...COMMON_START,
    { key: "teeth", label: "Teeth & their condition (FDI)", mandatory: true, hint: "e.g. 46 caries, 36 fractured", get: teeth },
    { key: "pain", label: "Pain", get: (c) => ve(c).exam?.pain },
    { key: "duration", label: "Duration of complaint", get: (c) => ve(c).exam?.duration },
    ...COMMON_END],
  tmj: [...COMMON_START,
    { key: "side", label: "Affected side", mandatory: true, hint: "Left, Right or Bilateral", get: (c) => tmj(c).side },
    { key: "duration", label: "Duration", get: (c) => tmj(c).duration },
    { key: "opening", label: "Mouth opening", get: (c) => tmj(c).opening },
    { key: "max_opening", label: "Maximum opening (mm)", get: (c) => txt(tmj(c).max_opening) },
    { key: "locking", label: "Jaw locking", get: (c) => tmj(c).locking },
    { key: "sounds", label: "Joint sounds", get: (c) => tmj(c).sounds },
    { key: "pain_score", label: "Pain score (0–10)", get: (c) => txt(tmj(c).pain_score) },
    { key: "trauma", label: "History of trauma", get: (c) => tmj(c).trauma },
    ...COMMON_END],
};

// Topics plus any other form question the admin marked required.
export function topicsFor(slug, form) {
  const base = VOICE_TOPICS[slug] || VOICE_TOPICS.general;
  const extra = (form?.questions || []).filter((q) => q.required && !/patient name|chief complaint/i.test(q.label))
    .map((q) => ({ key: `form:${q.id}`, label: q.label, mandatory: true, get: (c) => txt(c.form_answers?.[q.id]) }));
  return [...base, ...extra];
}

export const valueOf = (t, c) => (c.voice_answers?.[t.key] || "").trim() || txt(t.get(c)).trim();

const parseTeeth = (s) => [...String(s || "").matchAll(/\b([1-8][1-8])\b\s*([a-z][a-z ]{0,30})?/gi)].map((m) => ({ fdi: m[1], status: (m[2] || "").trim() }));
const side = (s) => (/bilat|both/i.test(s) ? "Bilateral" : /left/i.test(s) ? "Left" : /right/i.test(s) ? "Right" : s);

// Build the Final Review wizard from voice capture + typed answers (typed answers win).
export function wizardFromVoice(c, slug) {
  const a = c.voice_answers || {};
  const typedTeeth = parseTeeth(a.teeth).map((t) => ({ fdi: t.fdi, status: t.status || (slug === "implant" ? "Missing" : "Other"), confidence: 100 }));
  const base = { ...c, patient_name: a.name || c.patient_name, chief_complaint: a.complaint || c.chief_complaint,
    extracted_patient: { ...(c.extracted_patient || {}), name: a.name || c.extracted_patient?.name || (c.patient_name !== "New Patient" ? c.patient_name : "") },
    tooth_findings: [...(c.tooth_findings || []), ...typedTeeth.filter((t) => !(c.tooth_findings || []).some((x) => String(x.fdi) === t.fdi))],
    patient_expectations: a.expectations || c.patient_expectations,
    medical_history: a.medical ? [a.medical] : c.medical_history };
  const w = blankWizard(base);
  const v = c.voice_exam || {};
  const ex = w.exam[slug] || {};
  if (slug === "implant") { ex.smile_line = ["High", "Medium", "Low"].find((x) => new RegExp(x, "i").test(a.smile || v.exam?.smile_line || "")) || ""; }
  if (slug === "general") { ex.pain = a.pain || txt(v.exam?.pain); ex.duration = a.duration || txt(v.exam?.duration); }
  if (slug === "tmj") {
    const t = v.tmj || {};
    const sd = side(a.side || t.side || "");
    const snd = a.sounds || txt(t.sounds);
    const sound = /crepit/i.test(snd) ? "Crepitus" : /click|pop/i.test(snd) ? "Clicking" : /^(no|none)/i.test(snd) ? "None" : "";
    Object.assign(ex, { side: sd, duration: a.duration || txt(t.duration), opening: a.opening || txt(t.opening),
      max_opening: String(a.max_opening || t.max_opening || "").replace(/[^\d.]/g, ""), locking: a.locking || txt(t.locking),
      trauma: a.trauma || txt(t.trauma), pain_score: String(a.pain_score ?? t.pain_score ?? "").replace(/[^\d]/g, ""),
      ...(sound && sd !== "Left" ? { sounds_r: sound } : {}), ...(sound && (sd === "Left" || sd === "Bilateral") ? { sounds_l: sound } : {}) });
  }
  const allergies = a.allergies || txt(v.allergies);
  w.history = { ...w.history, allergies: /^(none|no|nil|nkda)/i.test(allergies) ? { none: true, list: [] } : { none: false, list: allergies ? allergies.split(/[,;]/).map((x) => x.trim()).filter(Boolean).map((substance) => ({ substance, reaction: "" })) : [] },
    meds: (a.medications || txt(v.medications)).split(/[,;]/).map((x) => x.trim()).filter(Boolean).map((name) => ({ name, dose: "", frequency: "" })),
    smoking: ((s) => (!s ? "" : /former|quit|\bex\b|stopped/i.test(s) ? "Former" : /^(no|none|never|non)/i.test(s) ? "None" : /unknown/i.test(s) ? "Unknown" : "Current"))(a.smoking || v.smoking || "") };
  return { ...w, step: 5, reached: 5, approved_at: null, stale: false, from_voice: true };
}
